import React, { Suspense, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import { Header } from '../components/Header';
import { LoadErrorComponent } from '../components/LoadErrorComponent';
import { FilterMultiSelectProps } from '../components/LogsMultiSelect';
import { Navigation } from '../components/Navigation';
import { SearchComponent } from '../components/SearchComponent';
import { SideBar } from '../components/SideBar';
import { SideBarContext } from '../context/SideBarContext';
import { Filter, TableComponentProps } from '../models/table-models';
import { PageSizeComponent } from '../components/PageSizeComponent';
import { Pagination, usePagination } from '../utils/pagination-hooks';
import { Button, Spinner } from 'react-bootstrap';
import { DateFilterComponentProps } from '../components/DateFilterComponent';

interface TableViewsControlProps extends FilterMultiSelectProps {
    pagination: Pagination;
}

interface TableViewsWrapperProps {
    tableComponent: (props: TableComponentProps) => JSX.Element;
    selectComponent?: ({ filter, setFilter }: FilterMultiSelectProps) => JSX.Element;
    dateFilterComponent?: ({ filter, setFilter, allowFutureDates }: DateFilterComponentProps) => JSX.Element;
    manualSearchControlComponent?: ({ filter, setFilter, pagination }: TableViewsControlProps) => JSX.Element;
    title: string;
    searchAvailable?: boolean;
    pagerAvailable?: boolean;
    manualSearch?: boolean;
    initialPageSize?: number;
    dateFilterAllowFuture?: boolean;
    /** Стойности, с които филтърът да се отвори. Ползва се за екрани, които не бива да тръгват от нулата. */
    initialFilter?: Partial<Filter>;
}

export const TableViewsWrapper = ({
    tableComponent,
    selectComponent,
    dateFilterComponent,
    manualSearchControlComponent,
    title,
    searchAvailable = true,
    pagerAvailable = true,
    manualSearch = false,
    initialPageSize = 10,
    dateFilterAllowFuture = false,
    initialFilter,
}: TableViewsWrapperProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { pathname } = useLocation();

    // Изчислява се веднъж при монтиране - useState пази само първата стойност, така че по-късните
    // преизчисления не местят вече избраното от потребителя.
    const buildInitialFilter = (): Filter => ( { tags: [], search: '', ...initialFilter } );

    const [filter, setFilter] = useState<Filter>(buildInitialFilter);
    const [appliedFilter, setAppliedFilter] = useState<Filter>(buildInitialFilter);
    const [hasSearched, setHasSearched] = useState(false);
    const [generalFiltersVisible, setGeneralFiltersVisible] = useState(true);
    const [searchVersion, setSearchVersion] = useState(0);
    const { sideBarVissible } = useContext(SideBarContext);
    const pagination = usePagination(initialPageSize);
    const previousPathname = useRef(pathname);
    const manualSearchFormId = 'table-manual-search-form';

    const TableComponent = tableComponent;
    const SelectComponent = selectComponent;
    const DateTimeFilterComponent = dateFilterComponent;
    const ManualSearchControlComponent = manualSearchControlComponent;
    const tableFilter = manualSearch ? appliedFilter : filter;

    /**
     * Обвивката е един и същ компонент за двайсетина екрана и React я преизползва при преход между тях -
     * елементът стои на същото място в дървото, само пропсовете се сменят. Тоест филтърът, страницата и
     * търсенето оцеляват прехода, а началните стойности от useState не се прилагат повторно.
     *
     * Оттам идваха два симптома на екрана с плащанията: датата от първо число се пренасяше в следващия
     * екран, а при влизане отдругаде изобщо не се появяваше, защото празният филтър оцеляваше.
     */
    useEffect(() => {
        if (previousPathname.current === pathname) return;

        previousPathname.current = pathname;
        setFilter(buildInitialFilter());
        setAppliedFilter(buildInitialFilter());
        setHasSearched(false);
        setGeneralFiltersVisible(true);
        pagination.setCurrentPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);

    /**
     * Нулира предпазителя при всяка смяна на филтъра, страницата или търсенето.
     *
     * use-async-resource пази и провалените заявки по параметри, тоест повторното рисуване със същия
     * филтър хвърля същата грешка наум, без нова заявка. Променено търсене е нов набор параметри и
     * тръгва наново - затова точно филтърът е признакът, по който екранът има смисъл да се опита пак.
     */
    const tableResetKey = JSON.stringify([tableFilter, pagination.currentPage, pagination.pageSize, searchVersion]);

    const applySearch = (searchValue?: string) => {
        const nextFilter = searchValue === undefined ? filter : { ...filter, search: searchValue };

        setAppliedFilter(nextFilter);
        setHasSearched(true);
        setSearchVersion((prev) => prev + 1);
    };

    return (
        <div className={`d-flex wrapper ${sideBarVissible && 'sidebar-active'} w-100`}>
            <SideBar>
                <Navigation />
            </SideBar>
            <div className='d-flex content-wrapper flex-column justify-content-start align-items-center w-100'>
                <Header title={t(title)} />
                <Suspense fallback={<Spinner animation='border' className='mt-3' />}>
                    <div className='d-flex flex-column px-4 mt-3 w-100 bg-light w-100 align-items-center flex-grow-1' style={{minHeight: 0, overflowY: 'auto'}}>
                        <div className={`${generalFiltersVisible ? 'd-flex' : 'd-none'} justify-content-between w-100 flex-wrap`}>
                            <div className='w-100 d-flex align-items-stretch align-items-lg-center justify-content-between flex-column flex-lg-row gap-2 table-filter-row'>
                                {DateTimeFilterComponent && (
                                    <DateTimeFilterComponent
                                        {...{ filter, setFilter }}
                                        allowFutureDates={dateFilterAllowFuture}
                                    />
                                )}
                                {SelectComponent && <SelectComponent {...{ filter, setFilter }} />}
                            </div>
                            <div className='w-100 d-flex justify-content-between align-items-stretch align-items-lg-start flex-column flex-lg-row gap-2 mb-3 mt-2 table-filter-row'>
                                <div className='d-flex flex-column align-items-stretch align-items-lg-start gap-2 table-filter-left'>
                                    {pagerAvailable && <PageSizeComponent {...{ pagination }} itemsToShow={[5, 10, 20, 50, 75, 100]} />}
                                    {manualSearch && searchAvailable && (
                                        <Button type='submit' form={manualSearchFormId} className='ms-2 px-4'>
                                            {t('searchButton')}
                                        </Button>
                                    )}
                                </div>
                                <div className='d-flex flex-column align-items-stretch align-items-lg-end gap-2 flex-grow-1 table-filter-right'>
                                    {searchAvailable && (
                                        <SearchComponent
                                            {...{ filter, setFilter, pagination }}
                                            applyOnChange={!manualSearch}
                                            onSearch={manualSearch ? applySearch : undefined}
                                            separateSubmitButton={manualSearch}
                                            hideSubmitButton={manualSearch}
                                            formId={manualSearch ? manualSearchFormId : undefined}
                                        />
                                    )}
                                    {manualSearch && searchAvailable && ManualSearchControlComponent && (
                                        <ManualSearchControlComponent {...{ filter, setFilter, pagination }} />
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* Предпазителят стои тук, а не в самите екрани: те четат ресурсите си в началото
                            на компонента, тоест собственото им ErrorBoundary е под мястото, където се
                            хвърля, и не хваща нищо. Оттук нататък провалена заявка оставя филтрите и
                            търсачката на екрана, вместо да събори приложението до бял екран. */}
                        <ErrorBoundary valueToObserve={tableResetKey} fallback={<LoadErrorComponent />}>
                            <TableComponent
                                filter={filter}
                                setFilter={setFilter}
                                pagination={pagination}
                                appliedFilter={tableFilter}
                                hasSearched={manualSearch ? hasSearched : true}
                                searchVersion={searchVersion}
                                setGeneralFiltersVisible={setGeneralFiltersVisible}
                            />
                        </ErrorBoundary>
                    </div>
                </Suspense>
            </div>
        </div>
    );
};
